import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Player } from "react-tuby";
import { Post, Caption } from "@/models";
import PreRollAd from "../../components/PreRollAd";
import "./player.css";

/**
 * Reads the `t` query parameter that subtitle-search results deep-link with.
 *
 * Accepts plain seconds ("754", what /search emits), clock time ("12:34",
 * "1:02:03") and the YouTube-style "1h2m3s" shorthand, so hand-edited or
 * shared links behave sensibly too. Returns null when there is nothing usable.
 */
function parseTimeParam(raw: string | null): number | null {
  if (!raw) return null;
  const value = raw.trim();

  if (/^\d+(\.\d+)?$/.test(value)) {
    const seconds = parseFloat(value);
    return Number.isFinite(seconds) ? seconds : null;
  }

  if (/^\d{1,3}(:[0-5]?\d){1,2}$/.test(value)) {
    return value
      .split(":")
      .reduce((total, part) => total * 60 + parseInt(part, 10), 0);
  }

  const shorthand = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
  if (shorthand && shorthand.slice(1).some((part) => part !== undefined)) {
    const [hours, minutes, seconds] = shorthand
      .slice(1)
      .map((part) => parseInt(part || "0", 10));
    return hours * 3600 + minutes * 60 + seconds;
  }

  return null;
}

function formatSubLabel(caption: Caption) {
  var label = caption.label;
  if (caption.type === "cp") label += " (official)";
  if (caption.type === "auto") label += " (auto)";
  if (caption.type === "fan") label += " (fan)";
  if (caption.fanName !== "") label += ` by ${caption.fanName}`;
  return label;
}

type VideoStatus = {
  status: string;
  percentDone?: number;
};

const VideoPlayer = ({
  post,
  thumbnail_url,
}: {
  post: Post;
  thumbnail_url: string;
}) => {
  const [loading, setLoading] = useState(true);
  const [videoAvailability, setVideoAvailability] = useState(false);
  const [video_url, setVideoUrl] = useState(
    "https://vlive-11.vlivearchive.com/placeholder.mp4"
  );
  const [videoStatus, setVideoStatus] = useState<VideoStatus | null>(null);
  const [adCompleted, setAdCompleted] = useState(false);
  const handleAdComplete = useCallback(() => setAdCompleted(true), []);

  const [searchParams] = useSearchParams();
  const startAt = parseTimeParam(searchParams.get("t"));
  // react-tuby drives its own controls off this ref when we pass it in, so we
  // and the library share one handle on the <video> element.
  const playerRef = useRef<HTMLVideoElement>(null);
  // Which start time we have already jumped to. Without this the effect below
  // would drag the viewer back to ?t= every time the component re-renders.
  const appliedSeekRef = useRef<number | null>(null);

  const seekTo = useCallback((target: number | null, force = false) => {
    const video = playerRef.current;
    if (video == null || target == null) return;
    if (!force && appliedSeekRef.current === target) return;
    // duration is NaN until metadata arrives, and we need it to clamp.
    if (!Number.isFinite(video.duration) || video.duration <= 0) return;
    appliedSeekRef.current = target;
    // Landing exactly on the end would immediately fire `ended`.
    video.currentTime = Math.min(
      Math.max(target, 0),
      Math.max(video.duration - 0.5, 0)
    );
  }, []);

  // Covers the case where metadata is already loaded and only `t` changed —
  // clicking a second search result for the video that is already playing
  // produces no fresh loadedmetadata event to ride.
  useEffect(() => {
    seekTo(startAt);
  }, [seekTo, startAt]);

  function etaAvailable(videoDurationSeconds: number): string {
    const FACTOR = 20;
    let secondsToProcess = Math.max(videoDurationSeconds / FACTOR, 40);
    if (secondsToProcess >= 60) {
      const minutes = Math.round(secondsToProcess / 60);
      return `${minutes} min`;
    } else {
      return `${Math.round(secondsToProcess)} sec`;
    }
  }

  useEffect(() => {
    const fetchVideoUrl = async () => {
      setLoading(true);
      if (post?.officialVideo?.videoSeq) {
        try {
          const url = `https://vlivearchive.com/api/download/${post.officialVideo.videoSeq}`;
          const response = await fetch(url);
          if (response.status === 404) {
            console.log("Video not found");
            setVideoAvailability(false);
            const url = `https://api.vlivearchive.com/video/${post.officialVideo.videoSeq}/status`;
            const response = await fetch(url);
            const data = await response.json();
            setVideoStatus(data);
          } else if (response.status === 200 || response.status === 302) {
            console.log("Video found");
            setVideoUrl(url);
            setVideoAvailability(true);
          }
        } catch (error) {
          console.error("Error fetching video URL:", error);
        }
      }
      setLoading(false);
    };

    fetchVideoUrl();
  }, [post]);

  async function requestVideo() {
    try {
      const response = await fetch(
        `https://api.vlivearchive.com/video/${post.officialVideo.videoSeq}/request`
      );
      const data = await response.json();
      if (data.status === "success") {
        console.log("Video requested");
        setVideoStatus({ status: "pending" });
        startPollingForStatus();
      }
    } catch (error) {
      console.error("Error requesting video:", error);
    }
  }

  function startPollingForStatus() {
    const intervalId = setInterval(async () => {
      if (post?.officialVideo?.videoSeq) {
        try {
          const url = `https://api.vlivearchive.com/video/${post.officialVideo.videoSeq}/status`;
          const response = await fetch(url);
          const data = await response.json();
          setVideoStatus(data);
          console.log(data);
          if (data.status === "available") {
            // setVideoAvailability(true);
            // setVideoUrl(
            //   `https://vlivearchive.com/api/download/${post.officialVideo.videoSeq}`
            // );
            clearInterval(intervalId);
            window.location.reload();
          }
        } catch (error) {
          console.error("Error fetching video status:", error);
        }
      }
    }, 3000);
  }

  var subtitles = post?.captions || [];

  // Pre-roll ad plays while video URL is fetched in the background
  if (!adCompleted) {
    return (
      <PreRollAd onComplete={handleAdComplete} poster={thumbnail_url} />
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 bg-gray-100">
        <div className="w-full max-w-2xl bg-gray-300 animate-pulse h-64 mb-4"></div>
        <div className="w-full max-w-2xl bg-gray-300 animate-pulse h-8 mb-2"></div>
        <div className="w-full max-w-2xl bg-gray-300 animate-pulse h-8 mb-2"></div>
        <div className="w-full max-w-2xl bg-gray-300 animate-pulse h-8 mb-2"></div>
        <p className="mb-4 text-lg text-gray-700">Loading...</p>
      </div>
    );
  }

  if (videoAvailability) {
    return (
      <Player
        src={video_url}
        poster={thumbnail_url}
        // react-tuby hardcodes alt="Tuby Poster"; patches/react-tuby+0.1.24.patch
        // adds this prop so the poster describes the actual video. The library
        // falls back to its own text when the title is empty.
        posterAlt={post.title}
        playerRef={playerRef}
        subtitles={subtitles.map((e) => ({
          url: `/subtitles/${e.file_name}`,
          lang: e.language,
          language: formatSubLabel(e),
        }))}
      >
        {/* Rendering the element ourselves is the only way to get at its
            events; `props` still carries react-tuby's own handlers and the
            subtitle <track>, so it must be spread first.

            The jump has to ride `loadeddata` specifically. react-tuby's own
            handler for that event resets currentTime to 0 unless it was given a
            playerKey, so anything we do on the earlier `loadedmetadata` gets
            stomped a moment later. Chaining after theirs, and forcing past the
            already-applied guard, puts us last. */}
        {(ref, props) => (
          <video
            ref={ref}
            {...props}
            onLoadedData={(event) => {
              props.onLoadedData?.(event);
              seekTo(startAt, true);
            }}
          />
        )}
      </Player>
    );
  }

  if (videoStatus?.status === "available_to_request") {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 bg-gray-100">
        <p className="mb-4 text-lg text-gray-700">
          Press button below to play the video. Estimated loading time:{" "}
          {etaAvailable(post.officialVideo.playTime)}.
        </p>
        <button
          onClick={requestVideo}
          className="px-4 py-2 font-semibold text-white bg-blue-500 rounded hover:bg-blue-700"
        >
          Play Video
        </button>
      </div>
    );
  } else if (videoStatus?.status === "video_not_found") {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 bg-gray-100">
        <p className="mb-4 text-lg text-gray-700">
          This video does not exist in our database.
        </p>
      </div>
    );
  } else if (videoStatus?.status === "missing") {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 bg-gray-100">
        <p className="mb-4 text-lg text-gray-700">
          This video is missing from our archives and cannot be requested. If
          you have a copy of the video, please contact us at
          admin@vlivearchive.com.
        </p>
      </div>
    );
  } else if (
    videoStatus?.status === "pending" ||
    videoStatus?.status === "queued"
  ) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 bg-gray-100">
        <div className="mb-4">
          <svg
            className="animate-spin h-5 w-5 text-gray-700"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
        <p className="mb-4 text-lg text-gray-700">Loading Video</p>
      </div>
    );
  } else if (videoStatus?.status === "in_progress") {
    return (
      <div className="flex flex-col items-center justify-center h-40 p-4 bg-gray-100">
        <p className="mb-4 text-lg text-gray-700">
          Processing video, please wait.
        </p>
        {videoStatus?.percentDone !== undefined && (
          <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
            <div
              className="bg-blue-600 h-2.5 rounded-full animate-pulse"
              style={{ width: `${videoStatus.percentDone}%` }}
            ></div>
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className="flex flex-col items-center justify-center h-40 p-4 bg-gray-100">
        <p className="mb-4 text-lg text-gray-700">
          There was an error fetching the video, and it is unknown if it is
          available to request. Please try again later.
        </p>
      </div>
    );
  }
};

export default VideoPlayer;
